import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

/** `testimonials.scroll_frequency` is INTEGER — empty string breaks MySQL; align with Testimonials2 `parseScrollFrequencyMs` (null = no autoplay). */
export function normalizeTestimonialsScrollFrequencyForDb(
  value: unknown
): number | null {
  if (value === undefined || value === null || value === "") return null;
  const raw = typeof value === "string" ? value.trim() : value;
  if (raw === "") return null;
  const n =
    typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export interface ITestimonials {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  card_color?: string;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  status?: boolean;
  variant?: string;
  metadata?: any[];
  // Variant 2 (Video Section)
  desktop_background_color?: string;
  mobile_background_color?: string;
  backvariant?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_color_4?: string;
  pre_title?: string;
  pre_title_color?: string;
  title_start_color?: string;
  title_end_color?: string;
  title_gradient_angle?: string;
  scroll_frequency?: number | null;
  button_text?: string;
  button_text_color?: string;
  button_color?: string;
  enable_animation?: boolean | number;
}

function applyScrollFrequencyToTestimonialsPayload<
  T extends Partial<ITestimonials>,
>(payload: T): T {
  if (!Object.prototype.hasOwnProperty.call(payload, "scroll_frequency")) {
    return payload;
  }
  return {
    ...payload,
    scroll_frequency: normalizeTestimonialsScrollFrequencyForDb(
      payload.scroll_frequency
    ),
  };
}

export async function createTestimonials(payload: Partial<ITestimonials>) {
  try {
    const { id: _omitId, ...rest } = payload;
    const body = applyScrollFrequencyToTestimonialsPayload(rest);
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.TESTIMONIALS.CREATE,
      body
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateTestimonials(id: number, payload: Partial<ITestimonials>) {
  try {
    const { id: _omitId, ...rest } = payload;
    const body = applyScrollFrequencyToTestimonialsPayload(rest);
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.TESTIMONIALS.UPDATE(id),
      body
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
